<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Navigation Header Component -->
    <NavHeader :dealerName="accountData && accountData[0]?.name ? accountData[0].name : dealerName" />
    
    <!-- Background Image with multiply blend effect (matching Form.vue) -->
    <div class="fixed inset-0 -z-30">
      <img src="/assets/photo.jpg" alt="Background" class="w-full h-full object-cover opacity-70 bg-image-animate" />
    </div>
    
    <!-- Primary multiply blend overlay for rich color interaction -->
    <div class="fixed inset-0 -z-20 bg-gradient-to-br from-blue-900/60 via-blue-900/60 to-gray-900/60 mix-blend-multiply"></div>
    
    <!-- Secondary overlay for better text readability -->
    <div class="fixed inset-0 -z-10 bg-black/20 backdrop-blur-[2px]"></div>

    <!-- Loader -->
    <div v-if="isLoading" class="isLoading flex justify-center items-center min-h-screen">
      <div role="status">
        <svg aria-hidden="true" class="w-14 h-14 text-gray-600 animate-spin dark:text-gray-600 fill-blue-600"
          viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor" />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill" />
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Dashboard Header -->
    <div class="relative z-1 pt-8 pb-4">
      <div class="max-w-[48rem] px-4 sm:px-6 lg:px-8 mx-auto">
        <div class="text-center mb-8">
          <div class="mb-3 inline-block px-4 py-1">
            <p class="text-blue-400 font-medium">Customer Welcome Portal</p>
          </div>
          <h1 class="text-3xl md:text-5xl font-bold text-white mb-3 bg-gradient-to-r from-blue-400 to-blue-500 bg-clip-text text-transparent">
            {{ accountData && accountData[0]?.name ? accountData[0].name : dealerName }}
          </h1>
          <p class="text-gray-300 text-lg">
            Track your performance and team activity
          </p>
        </div>
      </div>
    </div>

    <div v-if="accountData && accountData[0]?.form"
      class="max-w-[48rem] px-4 py-6 sm:px-6 lg:px-8 mx-auto relative z-1">

      <!-- Date Range Selection Component -->
      <modern-date-range-selector @date-range-changed="updateData" />

      <!-- Stats Cards with Enhanced Styling -->
      <div class="grid lg:grid-cols-2 gap-6 relative z-2 mb-8">
        <!-- Total Entries Count Component -->
        <div v-if="chartData && chartData.total_count">
          <total-entries-count :dateRange="selectedDateRange" :count="chartData.total_count" />
        </div>
        <div v-else class="flex flex-col gap-y-3 lg:gap-y-5 p-6 md:p-8 bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-2xl transform transition-all duration-300 hover:scale-105">
          <div class="text-center">
            <div class="mb-4">
              <div class="mx-auto w-16 h-16 bg-gradient-to-t from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <h3 class="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2">
              No Results
            </h3>
            <p class="text-gray-300 text-lg mb-4">at this time period.</p>
            <div class="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-4">
              <dl class="flex justify-center items-center divide-x divide-white/20">
                <dt class="pr-4">
                  <span class="block text-sm font-semibold text-gray-300">From: {{ selectedDateRange.startDate }}</span>
                </dt>
                <dd class="text-left pl-4">
                  <span class="block text-sm font-semibold text-gray-300">To: {{ selectedDateRange.endDate }}</span>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <!-- Additional Stats Card -->
        <div class="flex flex-col gap-y-3 lg:gap-y-5 p-6 md:p-8 bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-2xl transform transition-all duration-300 hover:scale-105">
          <div class="inline-flex justify-center items-center mb-2">
            <div class="w-3 h-3 inline-block bg-gradient-to-r from-green-400 to-green-600 rounded-full mr-3 animate-pulse"></div>
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-300">Performance</span>
          </div>
          <div class="text-center">
            <div class="relative">
              <h3 class="text-5xl sm:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                {{ performancePercentage }}%
              </h3>
              <div class="absolute -top-2 -right-2 w-4 h-4 bg-green-400 rounded-full animate-bounce"></div>
            </div>
            <p class="text-sm text-gray-300 mt-3 font-medium">Monthly Goal Progress</p>
            <div class="mt-4 w-full bg-black/40 backdrop-blur-sm rounded-full h-2 border border-white/10">
              <div 
                class="bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full transition-all duration-1000 ease-out"
                :style="{ width: `${performancePercentage}%` }"
              ></div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <div class="max-w-[48rem] px-4 pb-2 pt-6 sm:px-6 lg:px-8 mx-auto relative z-1">
      <!-- Chart Section with Enhanced Header -->
      <div v-if="chartData && chartData.total_count" class="space-y-6">
        <div class="transform transition-all duration-300 hover:scale-[1.02]">
          <chart-weekly :chartData="chartData.entries" :dateRange="selectedDateRange" />
        </div>
      </div>
      <div v-else class="text-center py-12">
        <div class="p-8 bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-2xl transform transition-all duration-300 hover:scale-105">
          <div class="mb-6">
            <div class="mx-auto w-20 h-20 bg-gradient-to-t from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
              <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          <h3 class="text-xl font-bold text-white mb-2">No data available</h3>
          <p class="text-gray-300 mb-6">Try selecting a different date range.</p>
        </div>
      </div>
    </div>

    <div v-if="chartData && chartData.total_count" class="max-w-[48rem] px-4 py-6 pt-6 sm:px-6 lg:px-8 mx-auto relative z-1">
      <!-- Sales Leaderboard Section with Enhanced Header -->
      <div class="space-y-6">
        <div class="flex items-center justify-between bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-6 shadow-2xl">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-gradient-to-t from-blue-500 to-pink-500 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 class="text-xl font-bold text-white">Team Performance</h3>
              <p class="text-sm text-gray-300">Top performers</p>
            </div>
          </div>
          <div class="flex items-center space-x-2">
            <div class="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
            <span class="text-sm font-medium text-gray-300">Active Team</span>
          </div>
        </div>
        <div class="transform transition-all duration-300 hover:scale-[1.02]">
          <sales-leader-board :dateRange="selectedDateRange" :entries="chartData.entries" />
        </div>
      </div>
    </div>

    <!-- Total Entries List Component -->
    <total-entries-list v-if="chartData && chartData.total_count" :dateRange="selectedDateRange"
      :entries="chartData.entries" />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import ModernDateRangeSelector from '@/components/ModernDateRangeSelector.vue'
import TotalEntriesCount from '@/components/TotalEntriesCount.vue'
import TotalEntriesList from '@/components/TotalEntriesList.vue'
import ChartWeekly from '@/components/ChartWeekly.vue'
import salesLeaderBoard from '@/components/salesLeaderboard.vue'
import NavHeader from '@/components/NavHeader.vue'
import axios from 'axios'

export default {
  name: 'welcome-dashboard-composition',
  components: {
    ModernDateRangeSelector,
    TotalEntriesCount,
    TotalEntriesList,
    ChartWeekly,
    salesLeaderBoard,
    NavHeader,
  },
  setup() {
    const store = useStore()
    const route = useRoute()

    // Reactive data
    const selectedDateRange = ref({
      startDate: '',
      endDate: ''
    })
    const chartData = ref(null)
    const isLoading = ref(false)

    // Computed properties
    const accountData = computed(() => store.state.accountData)
    const dealerName = computed(() => {
      // Get dealer name from slug - convert slug format to display format
      const slug = route.params.slug
      if (slug) {
        // Convert slug like "courtney-patterson-ford" to "Courtney Patterson Ford"
        return slug.split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      }
      return 'Dealership'
    })
    const performancePercentage = computed(() => {
      if (chartData.value && chartData.value.total_count) {
        // This should be updated to use dynamic goal calculation like the main dashboard
        // For now, keeping the monthly goal logic but this needs proper implementation
        return Math.round((chartData.value.total_count / 30) * 100)
      }
      return 0
    })

    // Methods
    const formatDate = (date) => {
      const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
      return formattedDate
    }

    const initializeDateRange = () => {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth()
      const startDate = new Date(currentYear, currentMonth, 1)

      selectedDateRange.value = {
        startDate: formatDate(startDate),
        endDate: formatDate(currentDate)
      }
    }

    const updateData = (newDateRange) => {
      selectedDateRange.value = newDateRange
      fetchData(newDateRange.startDate, newDateRange.endDate)
    }

    const fetchData = async (startDate, endDate) => {
      if (!accountData.value || !accountData.value[0]?.form) {
        console.warn('form_id not available yet. Retrying...')
        setTimeout(() => fetchData(startDate, endDate), 1000)
        return
      }

      isLoading.value = true

      try {
        const consumerKey = 'ck_e3232415b6abca7e07c5a308d4da5498c3d5008e'
        const consumerSecret = 'cs_9a8a31739ed97f04c422309918f4dd0e547c7d74'
        const authString = btoa(`${consumerKey}:${consumerSecret}`)

        const searchCriteria = encodeURIComponent(JSON.stringify({
          start_date: startDate,
          end_date: endDate
        }))

        const form_id = accountData.value[0]?.form
        const apiUrl = `https://engagr.driveagent.io/wp-json/gf/v2/forms/${form_id}/entries?search=${searchCriteria}&paging[page_size]=100`

        const response = await axios.get(apiUrl, {
          headers: {
            'Authorization': `Basic ${authString}`,
          },
        })

        chartData.value = response.data
      } catch (error) {
        console.error('Failed to fetch data:', error)
      } finally {
        isLoading.value = false
      }
    }

    // Watchers
    watch(accountData, (newVal) => {
      if (newVal && newVal[0]?.form) {
        fetchData(selectedDateRange.value.startDate, selectedDateRange.value.endDate)
      }
    })

    // Lifecycle
    onMounted(async () => {
      initializeDateRange()
      await store.dispatch('fetchAccountData', route.params.slug)
      if (accountData.value && accountData.value[0]?.form) {
        fetchData(selectedDateRange.value.startDate, selectedDateRange.value.endDate)
      }
    })

    return {
      // Reactive data
      selectedDateRange,
      chartData,
      isLoading,
      
      // Computed
      accountData,
      dealerName,
      performancePercentage,
      
      // Methods
      updateData,
      formatDate
    }
  }
}
</script>

<style scoped>
/* Custom animations and effects */
@keyframes glow {
  0%, 100% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.5); }
  50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 30px rgba(59, 130, 246, 0.6); }
}

.animate-glow {
  animation: glow 2s ease-in-out infinite;
}

.isLoading{
  z-index: 99999999999;
}

/* Smooth transitions for all interactive elements */
* {
  transition: all 0.2s ease-in-out;
}

/* Enhanced hover effects */
.hover-lift:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

/* Gradient text animation */
.gradient-text {
  background: linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  animation: gradient-shift 3s ease infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@keyframes gradient-shift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Card hover effects */
.card-hover {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-hover:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

/* Loading animation enhancement */
.loading-dots {
  display: inline-block;
}

.loading-dots::after {
  content: '';
  animation: dots 1.5s steps(5, end) infinite;
}

@keyframes dots {
  0%, 20% { content: ''; }
  40% { content: '.'; }
  60% { content: '..'; }
  80%, 100% { content: '...'; }
}

/* Background image subtle zoom animation */
@keyframes subtle-zoom {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.bg-image-animate {
  animation: subtle-zoom 30s ease-in-out infinite;
}
</style> 